// The backend owns the settings vocabulary: restaurant_settings rows resolved by
// GET /api/settings/resolved, whose envelope is {context, settings: {key: {value,
// source_scope, overridden}}}. db/offlineDb.js flattens that to {key: value} — the
// same flat shape GET /api/settings/display used to return — and hands it here.
// Vibez's UI reads a different camelCase shape. Map one onto the other here
// rather than renaming backend keys, which would break the schema-driven admin
// panel and the backend test suite.
//
// Only a few of Vibez's settings have a real backend counterpart. The rest
// keep their defaults, and that is recorded honestly below rather than papered over
// with plausible-looking key names that would silently fall back.
//
// Parsing rules match what the previous frontend enforced; see
// docs/frontend-billing-contracts.md §3.

const asBool = (value, fallback) =>
  value === undefined || value === null || value === ""
    ? fallback
    : String(value).toLowerCase() === "true";

const asNumber = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export function mapDisplaySettings(display, defaults) {
  if (!display || typeof display !== "object") return defaults;

  // The flat payload carries service_charge_enabled; the only faithful reading is:
  // disabled means zero, enabled keeps the configured default. The percentage rides
  // through under its own key (service_charge_pct) for whoever wants the rate.
  const serviceChargeEnabled = asBool(display.service_charge_enabled, true);

  return {
    ...defaults,
    restaurantName: display.restaurant_name || defaults.restaurantName,
    gst: asNumber(display.default_tax_percent, defaults.gst),
    receiptFooter: display.bill_footer_1 || defaults.receiptFooter,
    serviceCharge: serviceChargeEnabled ? defaults.serviceCharge : 0,

    // The discount cap the server actually enforces. `discount_max_pct` IS in the
    // config registry, and billing_service._compute_bill rejects a bill past it —
    // but nothing on the client ever read it, so DiscountForm asserted "Cap 100%"
    // on an outlet capped at 20 and the refusal only surfaced as a 400 with the
    // guest at the till. Left undefined when the server did not send the key, so
    // the form can say nothing rather than invent a limit.
    discountMaxPct: asNumber(display.discount_max_pct, defaults.discountMaxPct),

    // No backend counterpart in the resolved payload as of Stage 41:
    //   autoKOT, allowNegativeStock (no inventory module), tablePrefix,
    //   realtimeEnabled (client-side toggle only).
    // They fall through from `defaults` via the spread above. The KDS thresholds
    // (kds_warn_minutes, kds_alert_minutes, …) DO arrive from the server and are
    // read by KDSPage under their own snake_case names — offlineDb keeps them in
    // the terminal document as they came, so they need no camelCase alias here.
  };
}

// ─── Write side: what the Admin page is allowed to edit ─────────────────────
//
// GET /api/settings/metadata is now the registry of every key that may be bound to
// a control: one ConfigMetadata per key ({key, display_name, description, type,
// scope, default, tier, domain, category, …}). PUT /api/settings/{key} takes
// { value: "<string>", scope_type, scope_id } and 404s on an unknown key — there is
// no collection-level PUT, which is why the old
// `axios.put(`${API}/settings`, { settings })` could only ever 405.
//
// The static ADMIN_SECTIONS / ADMIN_SETTING_KEYS tables that used to live here are
// gone: the registry is the single source of the information architecture (domain →
// category → key), and a backend registry test guards the invariant that every row
// is accounted for. What survives here is what the registry does NOT carry: the
// audited exclusion list, the owner's vocabulary, and the search that joins them.

/**
 * Every restaurant_settings row deliberately NOT bound to a control, and why.
 * Three failure modes, all of which put a lie on the screen:
 *   "no reader"   — the row exists but no backend code path consults it, so the
 *                   control would change nothing.
 *   "no producer" — a reader exists, but the value it needs is never assembled,
 *                   so the switch still cannot move a byte on paper.
 *   "superseded"  — a real column outranks the row, so the control would be read
 *                   back over by hardware config the owner sets elsewhere.
 * `searchAdmin` refuses to return any of these even when the registry advertises
 * them, so a search can never send the owner to a decorative control.
 *
 * This list is a claim about backend code, so it goes stale silently the moment
 * somebody wires a key up. It has been re-audited against the reader named in
 * each reason; nine keys left it in that pass (address, service_charge_label,
 * bill_show_tax_breakup, bill_show_discount, bill_show_service_charge,
 * kot_show_table, kot_show_item_notes, kot_show_modifiers, report_pdf_page_size)
 * because document_service / escpos / reports.py now genuinely consult them.
 * Re-audit before adding, and cite the file:line that reads — or fails to read —
 * the row, so the next reader can check the claim instead of inheriting it.
 *
 * backend/tests/test_config_registry_invariants.py mirrors the subset of these
 * that carry no ConfigMetadata at all (`_UNREGISTERED_SETTING_KEYS`) and fails
 * CI on any restaurant_settings row that is in neither list — so a new orphan
 * cannot be introduced without a stated reason. Keep the reasons in step. The
 * lists agree key-for-key: bill_show_payment_method left this list (its producer
 * landed — close_order passes the tenders into the snapshot), so every key here
 * is unregistered and `_UNREGISTERED_SETTING_KEYS` matches exactly.
 */
export const EXCLUDED_SETTING_KEYS = {
  default_tax_percent: "no reader — a bill's tax is the item's TaxRule, falling back to menu_items.tax_percent (services/tax_service.resolve). mapDisplaySettings aliases it to `gst` and no screen reads that either.",
  post_bill_editing_enabled: "no reader — zero occurrences in backend/app; post-bill editing is gated by order status and permissions.",
  post_bill_reprint_required: "no reader — zero occurrences in backend/app; see lib/orderHistoryApi.js.",
  attendance_normal_minutes: "superseded — managed via attendance policy service /api/attendance/policies/outlet.",
  attendance_half_day_max_minutes: "superseded — managed via attendance policy service /api/attendance/policies/outlet.",
  pin_max_attempts: "superseded — managed via PIN security service (services/pin_service.py).",
  pin_lockout_minutes: "superseded — managed via PIN security service (services/pin_service.py).",
  sessions_valid_after: "superseded — runtime token cutoff timestamp set exclusively by /api/users/sign-out-everyone.",
  kot_show_captain_name: "no reader — the KOT captain line is decided by kot_show_captain (printing/escpos._show_captain_on_kot), which does have a control.",
  bill_copies: "superseded — print_service prints `target_printer.bill_copies` copies, per printer.",
  default_kot_copies: "superseded — print_service prints `printer.kot_copies` copies, per printer.",
  default_printer_port: "superseded — a printer's port is the printers.port column, which print_service passes to the job.",
};

// A caveat on three of the keys that just left this list: printing/pdf.py is the
// other renderer of a BillDocument (api/orders.py rehydrates document_snapshot
// into it for the PDF export) and reads none of the display switches. So
// bill_show_tax_breakup / bill_show_discount / bill_show_service_charge and the
// address line take effect on the thermal slip and are ignored on the PDF. The
// controls are honest — the setting does change the bill the guest is handed —
// but an owner who switches the GST breakup off will still see it on a PDF
// re-export until pdf.py reads the same four fields.

// Routing concepts that were REMOVED from the schema and must not come back as
// screens: printer_routing_rules (dropped by migration 025, table added in 006),
// the extra_printer_groups JSONB columns (018) and every printer_group column
// (017); migration 026 deleted the matching settings rows. users.bill_printer_mode
// still exists as a column with a self-service endpoint, but no code reads it.
// A control for any of these would change no printer's behaviour. Kept as a note
// rather than an export: the config registry is where a key earns a control now,
// and none of these are in it.

/**
 * The non-settings capabilities each section owns. Two jobs: they make the
 * search useful for things that are not restaurant_settings rows (a printer's
 * font size is a printers column, but an owner still searches "font"), and they
 * are the written record that every reachable capability has a home.
 *
 * `section` is a tab id AdminPage owns; a hit carries it so the page can jump.
 * AdminPage resolves an unknown id by staying put, so a panel it has not (yet)
 * rebuilt still surfaces in search instead of vanishing from it.
 */
export const ADMIN_PANELS = [
  {
    id: "printer-registry", section: "printers", where: "Printer registry",
    label: "Printers — name, IP, port, copies, paper width, font",
    synonyms: "printer ip address port ethernet hardware kot copies bill copies column width paper font size bold style header location enable disable",
  },
  {
    id: "print-routing", section: "printers", where: "Print routing",
    label: "Print routing — item → printer, table → bill printer, cashier lock",
    synonyms: "routing route menu item printer kitchen counter table bill printer cashier lock fan out mapping",
  },
  {
    id: "floor-designer", section: "floor", where: "Floor designer",
    label: "Floor plan, areas and tables",
    synonyms: "table floor area section seats layout designer capacity",
  },
  {
    id: "staff-accounts", section: "staff", where: "Staff",
    label: "Staff accounts and PINs",
    synonyms: "user employee captain cashier admin pin login password deactivate",
  },
  {
    id: "permissions", section: "staff", where: "Permissions",
    label: "Role permission matrix",
    synonyms: "permission role access rights grant revoke matrix",
  },
  {
    id: "menu-catalogue", section: "menu", where: "Menu catalogue",
    label: "Menu items, prices and per-item tax",
    synonyms: "menu item price category veg non-veg tax percent 86 availability short name",
  },
  {
    id: "combos", section: "menu", where: "Combos",
    label: "Combos and meal bundles",
    synonyms: "combo meal bundle deal set",
  },
  {
    id: "settings-portability", section: "data", where: "Import & export",
    label: "Settings import, export and backup",
    synonyms: "backup restore export import json migrate petpooja data portability",
  },
  {
    id: "reports-panel", section: "reports", where: "Reports",
    label: "Sales and operations reports",
    synonyms: "report sales daypart eod summary analytics revenue",
  },
  {
    id: "custom-reports", section: "reports", where: "Custom reports",
    label: "Build your own report: data, columns, filters and sort",
    synonyms: "custom report builder columns sort filter sequence own report",
  },
  {
    id: "ai-assistant", section: "integrations", where: "AI Assistant",
    label: "AI Assistant (MCP Server) tokens & access",
    synonyms: "ai assistant mcp model context protocol token claude cursor antigravity api keys tokens",
  },
  {
    id: "health-strip", section: "system", where: "Health",
    label: "Server, database, print worker and terminal health",
    synonyms: "health status online offline database print worker terminal websocket diagnostics heartbeat",
  },
  {
    id: "terminal-prefs", section: "system", where: "Terminal preferences",
    label: "This terminal's live-sync switch",
    synonyms: "websocket realtime live sync terminal local troubleshooting",
  },
];

/**
 * The specialized modules the admin tree mounts beside the registry domains.
 *
 * These are NOT restaurant_settings rows and never were — a printer's IP, the
 * floor plan, the menu, the staff list and the print-worker heartbeat are tables
 * of their own. They hang off the same tree so the owner has one place to look,
 * and their ids are the same ids `ADMIN_PANELS[].section` points at, so a search
 * hit on a panel resolves to a tree node rather than to nowhere.
 *
 * Order is the order they appear under "Management". Keep it in step with
 * ADMIN_PANELS: the AdminPage test asserts every panel section has a module.
 */
export const ADMIN_MODULES = [
  { id: "menu", title: "Menu Catalogue", summary: "Items, categories, prices, per-item tax and combos." },
  { id: "floor", title: "Floor Designer", summary: "Areas, tables and the layout the Captain app draws." },
  { id: "lists", title: "Order Lists", summary: "Payment modes, cancellation reasons, order types and who may move or cancel an order." },
  { id: "printers", title: "Printers & Routing", summary: "The ESC/POS registry and what prints where." },
  { id: "staff", title: "Staff & Permissions", summary: "Accounts, PINs and the role permission matrix." },
  { id: "reports", title: "Reports", summary: "Sales, daypart and end-of-day reporting." },
  { id: "data", title: "Data Portability", summary: "Settings import, export and backup." },
  { id: "integrations", title: "Integrations", summary: "Telegram, RestoYaar Connect, and AI Assistant (MCP)." },
  { id: "system", title: "System Health", summary: "Server, database, print worker and terminal probes." },
  { id: "logs", title: "Device Logs", summary: "Every terminal's interaction log, force sync and date-wise downloads." },
];

/**
 * The order the registry's domains are presented in — operational, not
 * alphabetical: an owner opens Admin to change the outlet's name or a bill line
 * far more often than to change a worker poll interval. A domain the registry
 * grows that is not listed here is appended alphabetically rather than dropped,
 * so a new domain surfaces without a frontend change.
 */
export const ADMIN_DOMAIN_ORDER = ["Restaurant", "Operations", "Billing", "Printing", "KDS", "Hardware", "System"];

/** Registry domains in presentation order, unknown ones alphabetical at the end. */
export function orderDomains(names) {
  const known = ADMIN_DOMAIN_ORDER.filter(name => names.includes(name));
  const rest = names.filter(name => !ADMIN_DOMAIN_ORDER.includes(name)).sort();
  return [...known, ...rest];
}

/**
 * Owner's words → the key's words. Searching is the only consumer, so a row for a
 * key the registry does not (yet) carry is inert rather than wrong: the registry
 * decides what exists, this table only decides what finds it.
 */
const SETTING_SYNONYMS = {
  restaurant_name: "outlet hotel brand header title",
  business_name: "legal entity company firm",
  address: "street location area pin code landmark shop premises where we are",
  phone: "contact telephone mobile number",
  gstin: "gst tax registration number",
  fssai: "licence license food safety number",
  currency_symbol: "rupee money ₹ rs",
  currency_render: "rupee symbol glyph money",
  locale: "language region format",
  invoice_prefix: "bill number series prefix invoice",
  order_types_enabled: "dine in takeaway parcel delivery channel",
  order_type_label: "dine in caption kot bill",
  service_charge_enabled: "tip gratuity service charge",
  service_charge_pct: "tip gratuity service charge percent",
  service_charge_label: "service charge name caption rename wording call it tip gratuity",
  charge_packing_takeaway: "packing packaging parcel takeaway box",
  charge_delivery: "delivery courier rider",
  round_off_enabled: "rounding round off nearest rupee paise",
  discount_enabled: "discount concession offer",
  discount_max_pct: "discount cap limit maximum concession",
  loyalty_enabled: "points rewards loyalty",
  loyalty_points_per_amount: "points rewards earn rate loyalty",
  loyalty_redeem_value_per_point: "points rewards redeem value loyalty",
  bill_footer_1: "footer receipt bill message thank you",
  bill_footer_2: "footer receipt bill message",
  bill_footer_3: "footer receipt bill message",
  bill_footer_4: "footer receipt bill message",
  // The three bill display switches. An owner asks for these as "gst lines on
  // bill" or "hide the discount", never as bill_show_*, so the words that find
  // them have to be the owner's — including "hide", which appears in neither the
  // key nor the server's display name.
  bill_show_tax_breakup: "gst lines on bill cgst sgst tax breakup split show hide invoice",
  bill_show_discount: "discount line on bill concession offer show hide",
  bill_show_service_charge: "service charge line on bill tip gratuity show hide",
  receipt_columns: "paper width columns 58 80 mm receipt font",
  default_paper_width: "paper width columns 58 80 mm receipt font",
  cgst_percent: "gst tax cgst vat",
  sgst_percent: "gst tax sgst vat",
  printer_cut_mode: "paper cut partial full guillotine printer",
  cash_drawer_enabled: "cash drawer kick open till",
  default_printer_id: "printer fallback default routing",
  print_max_attempts: "printer retry attempts spooler queue",
  print_backoff_cap_s: "printer retry backoff spooler queue",
  print_tcp_timeout_s: "printer timeout tcp network",
  print_heartbeat_s: "printer worker keepalive heartbeat",
  printer_health_interval_s: "printer health check interval",
  worker_poll_interval_s: "printer worker poll queue",
  worker_heartbeat_interval_s: "printer worker heartbeat",
  worker_stale_threshold_s: "printer worker stale health",
  kds_warn_minutes: "kitchen display kds timer amber warn colour",
  kds_alert_minutes: "kitchen display kds timer red alert colour",
  kds_sound_enabled: "kitchen display kds sound beep chime",
  kds_new_ticket_flash_s: "kitchen display kds flash new ticket",
  kds_recall_window_s: "kitchen display kds recall undo bump",
  kot_header: "kot slip header running table title kitchen",
  kot_show_captain: "kot captain steward waiter kitchen",
  print_kot_on_cancel: "void slip cancellation slip cancel print kot kitchen",
  kot_cancel_copies: "cancel void slip copies duplicate kot kitchen",
  auto_print_bill_on_settle: "auto print bill payment settle automatic receipt print after",
  kot_show_number: "kot number serial slip kitchen show hide",
  kot_show_date: "kot date time stamp slip kitchen show hide",
  slip_time_format: "time format clock 12h 24h am pm printed bill kot slip",
  kot_custom_message: "custom message note standing line fire kitchen kot",
  round_off_increment: "round off increment rupee paise 50 nearest bill total",
  feedback_enabled: "feedback rating guest review collect complaint",
  feedback_low_rating_threshold: "low bad rating threshold star review alert",
  table_lock_ttl_seconds: "table lock hold lifetime takeover floor captain seconds",
  kds_bump_targets: "kds bump allowed served preparing kitchen ticket transition",
  bill_show_customer: "customer name company gstin on bill show hide guest",
  // What the kitchen slip carries. "kitchen slip" is the phrase a cook and an
  // owner both use for a KOT, so each of these answers to it as well as to "kot".
  kot_show_table: "kot kitchen slip table number floor line show hide",
  kot_show_item_notes: "kot kitchen slip notes special note instruction comment remark less spicy show hide",
  kot_show_modifiers: "kot kitchen slip modifier add on extra option choice topping show hide",
  report_dayparts: "daypart breakfast lunch dinner evening report band",
  report_pdf_page_size: "report pdf paper size page size a4 a5 letter export download sheet",
  idempotency_expiry_hours: "idempotency replay key expiry",
  lock_cleanup_interval_s: "lock cleanup table advisory",
  login_rate_limit_per_min: "login security rate limit brute force",
  pg_reconnect_s: "postgres listener reconnect database",
  request_body_limit_bytes: "request upload size limit payload",
};

/** `restaurant_name` → `Restaurant Name`, for search before the registry loads. */
const humanize = key =>
  key.replace(/_/g, " ").replace(/\b[a-z]/g, character => character.toUpperCase());

/**
 * GET /api/settings/metadata returns a list. Accept the keyed form too, so a caller
 * that already indexed the registry by key does not have to un-index it first.
 */
const asMetadataList = registry => {
  if (Array.isArray(registry)) return registry.filter(Boolean);
  if (registry && typeof registry === "object") {
    return Object.entries(registry).map(([key, meta]) =>
      meta && typeof meta === "object" ? { key, ...meta } : { key }
    );
  }
  return [];
};

/**
 * One search over the whole admin surface: registry settings AND the panels,
 * matched on key, the server's own display name, the domain/category it sits in,
 * and synonyms. Synonyms are what make it usable by an owner rather than by
 * whoever named the column — "font" has to find the printer editor, "gratuity"
 * has to find the service-charge rate.
 *
 * `registry` is the GET /api/settings/metadata response; the server's display_name
 * wins over the humanised key so the result reads the same as the control it jumps
 * to. A key on the audited exclusion list is never returned, whatever the registry
 * says about it — a control that changes nothing must not be findable.
 *
 * Ranking, in the order a searching owner expects: an exact hit on the key or the
 * label first, then a hit inside either of them, then a hit that only the synonyms
 * or the description caught. Settings outrank panels at equal score (the sort is
 * stable and settings are collected first), as in the pre-registry implementation.
 */
export function searchAdmin(query, registry = []) {
  const needle = String(query || "").trim().toLowerCase();
  if (needle.length < 2) return [];

  const scored = [];
  const rank = (key, label) => {
    const loweredKey = String(key || "").toLowerCase();
    const loweredLabel = String(label || "").toLowerCase();
    if (loweredKey === needle || loweredLabel === needle) return 3;
    if (loweredKey.includes(needle) || loweredLabel.includes(needle)) return 2;
    return 1;
  };

  for (const meta of asMetadataList(registry)) {
    const key = meta.key;
    if (typeof key !== "string" || !key) continue;
    if (key in EXCLUDED_SETTING_KEYS) continue;
    const label = meta.display_name || meta.label || humanize(key);
    const hay = `${key} ${label} ${meta.description || ""} ${meta.domain || ""} ${meta.category || ""} ${SETTING_SYNONYMS[key] || ""}`.toLowerCase();
    if (!hay.includes(needle)) continue;
    scored.push({
      score: rank(key, label),
      hit: {
        id: `setting:${key}`, kind: "setting", key, label,
        sectionId: meta.domain || "", sectionTitle: meta.domain || "", where: meta.category || "",
        // `where` doubles as a human label for panels, so the tree navigation
        // reads the category off its own field rather than off a field that
        // means two things.
        categoryId: meta.category || "",
      },
    });
  }

  for (const panel of ADMIN_PANELS) {
    const hay = `${panel.id} ${panel.label} ${panel.where} ${panel.synonyms}`.toLowerCase();
    if (!hay.includes(needle)) continue;
    scored.push({
      score: rank(panel.id, panel.label),
      hit: {
        id: `panel:${panel.id}`, kind: "panel", label: panel.label,
        sectionId: panel.section, sectionTitle: humanize(panel.section), where: panel.where,
        categoryId: "",
      },
    });
  }

  return scored.sort((a, b) => b.score - a.score).map(entry => entry.hit);
}

/** Keys whose draft value differs from the value the server last confirmed. */
export function changedSettingKeys(draft, baseline) {
  return Object.keys(draft).filter(key => draft[key] !== baseline[key]);
}

// A saved server value that the terminal cache also carries under a camelCase
// name. Mirroring on save keeps the screens still reading the Dexie document
// (AppShell's title, the bill footer preview) in step with what was just written,
// instead of drifting until the next bootstrap. The KDS thresholds are NOT here:
// they live in the terminal document under their own snake_case names, written by
// the bootstrap cache path in db/offlineDb.js, which is their one owner.
const LOCAL_MIRROR = {
  restaurant_name: ["restaurantName", value => value],
  bill_footer_1: ["receiptFooter", value => value],
  service_charge_pct: ["serviceCharge", value => asNumber(value, 0)],
};

/** `{backendKey: savedValue}` → the camelCase patch for the local settings doc. */
export function mirrorToLocalSettings(saved) {
  const patch = {};
  for (const [key, value] of Object.entries(saved || {})) {
    const mirror = LOCAL_MIRROR[key];
    if (mirror) patch[mirror[0]] = mirror[1](value);
  }
  return patch;
}
